import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  useInfiniteNotifications,
  useMarkNotificationAsReadMutation,
} from "./notification.api";
import { Notification } from "./types";

interface NotificationContextType {
  notificationQueue: Notification[];
  dismissNotification: (id: string) => void;
  isLoading: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  error: Error | null;
  addNotificationQueue: (newNotification: any) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

const NOTIFICATION_QUEUE_THRESHOLD = 5;

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notificationQueue, setNotificationQueue] = useState<Notification[]>(
    []
  );
  const { mutate: markAsRead } = useMarkNotificationAsReadMutation();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteNotifications({
    is_read: false,
  });

  // Process API notifications into the queue
  useEffect(() => {
    if (data) {
      const allNotifications = data.pages.flatMap((page) => page.results);
      setNotificationQueue(allNotifications);
    }
  }, [data]);

  // When the top notification changes, mark it as read
  useEffect(() => {
    if (notificationQueue.length > 0 && !notificationQueue[0].is_read) {
      markAsRead(notificationQueue[0].id);

      setNotificationQueue((prev) =>
        prev.map((notification, index) =>
          index === 0 ? { ...notification, is_read: true } : notification
        )
      );
    }
  }, [notificationQueue, markAsRead]);

  const addNotificationQueue = useCallback((newNotification: any) => {
    setNotificationQueue((prev) => [prev, ...newNotification]);
  }, []);

  const dismissNotification = useCallback(
    (id: string) => {
      setNotificationQueue((prev) => prev.filter((n) => n.id !== id));

      if (
        notificationQueue.length < NOTIFICATION_QUEUE_THRESHOLD &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    },
    [notificationQueue.length, hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const value = {
    notificationQueue,
    dismissNotification,
    isLoading,
    hasNextPage: !!hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    error: error as Error | null,
    addNotificationQueue,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider"
    );
  }
  return context;
};
